import { Close } from '@icons/index';
import { Button, Divider, Drawer, IconButton, Stack, Typography } from '@mui/material';
import {
    AuditOperationStepTypeEnum,
    AuditOperationTypeEnum,
    AuditOwnedByEnum,
    CreateAuditOperation
} from '@store/audits/audits-operation/audits-operation.model';
import { auditsOperationService } from '@store/audits/audits-operation/audits-operation.service';
import { FieldValidationType, checkIfErrors, getFieldError } from '@utils/yup.utils';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import AuditContainer from '../audit/components/AuditContainer';

interface AddPrecoContainerDrawerProps {
    isOptimization?: boolean;
    isValidation?: boolean;
}


const AddPrecoContainerDrawer = (props: AddPrecoContainerDrawerProps) => {
    const { isOptimization} = props;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { auditId } = useParams();
    const { state } = useLocation();
    const auditData = state as unknown as { curentAuditOperation: CreateAuditOperation };
    const { enqueueSnackbar } = useSnackbar();

    const [auditOperation, setAuditOperation] = useState<CreateAuditOperation>({
        type: AuditOperationTypeEnum.CLASSIC
    });

    const handleClose = () => navigate(-1);

    const errors = {
        container: getFieldError(auditOperation?.containerId || auditOperation?.containerCustom, FieldValidationType.REQUIRED_STRING),
        quantity: getFieldError(auditOperation?.quantity, FieldValidationType.REQUIRED_NUMBER),
        by: getFieldError(auditOperation?.by, FieldValidationType.REQUIRED_STRING),
        waste: getFieldError(auditOperation?.wasteId || auditOperation?.wasteCustom, FieldValidationType.REQUIRED_STRING),
        whereIs: getFieldError(auditOperation?.whereIs, FieldValidationType.STRING_LONG),
    };

    useEffect(() => {
        if (auditId) {
            updateAuditOperation('auditId', Number(auditId));
            updateAuditOperation('stepType', AuditOperationStepTypeEnum.PRECO);
        }
    }, [auditId]);

    useEffect(() => {
        if (auditId && (isOptimization)) {
            updateAuditOperation('type', auditData.curentAuditOperation.type);
            updateAuditOperation('containerId', auditData.curentAuditOperation.containerId);
            updateAuditOperation('containerCustom', auditData.curentAuditOperation.containerCustom);
            updateAuditOperation('container', auditData.curentAuditOperation.container);
            updateAuditOperation('quantity', auditData.curentAuditOperation.quantity);
            updateAuditOperation('by', auditData.curentAuditOperation.ownedBy);
            updateAuditOperation('description', auditData.curentAuditOperation.description);
            updateAuditOperation('whereIs', auditData.curentAuditOperation.description);
            updateAuditOperation('wasteId', auditData.curentAuditOperation.wasteId);
            updateAuditOperation('wasteUnitName', auditData.curentAuditOperation.wasteUnitName);
            updateAuditOperation('annualEstimatedWeightInTons', auditData.curentAuditOperation.annualEstimatedWeightInTons);
            updateAuditOperation('stepType', auditData.curentAuditOperation.stepType);
            updateAuditOperation('waste', auditData.curentAuditOperation.waste);
            updateAuditOperation('wasteCustom', auditData.curentAuditOperation.wasteCustom);
            updateAuditOperation('schedule', auditData.curentAuditOperation.schedule);
            updateAuditOperation('documents', auditData.curentAuditOperation.documents);
        }
    }, [auditId, isOptimization, auditData]);

    const updateAuditOperation = <T extends keyof CreateAuditOperation>(
        field: T,
        value: CreateAuditOperation[T]
    ) => {
        setAuditOperation((prevAuditOperation) => {
            if (field === 'frequency') {
                const prevFrequency = prevAuditOperation.frequency ?? {};
                const updatedFrequency = {
                    ...prevFrequency,
                    ...(prevFrequency as any),
                    ...(value as any)
                };
                return {
                    ...prevAuditOperation,
                    frequency: updatedFrequency
                };
            } else {
                return {
                    ...prevAuditOperation,
                    [field]: value
                };
            }
        });
    };

    const createAuditOperation = () => {
        const auditOPData: CreateAuditOperation = {
            ...auditOperation,
            by: auditOperation.by ?? AuditOwnedByEnum.SUPPLIER,
            stepType: AuditOperationStepTypeEnum.PRECO
        };

        if (auditOperation) {
            auditsOperationService.createAuditOperation(auditOPData).subscribe({
                next: () => {
                    enqueueSnackbar(t('settings.containers.success.add'), { variant: 'success' });
                    handleClose();
                },
                error: (err) => enqueueSnackbar(err.text, err.options),
            });
        }
    };

    const optimizeAuditOperation = () => {
        const auditOPData: CreateAuditOperation = {
            ...auditOperation,
            by: auditOperation.by ?? AuditOwnedByEnum.SUPPLIER,
            stepType: AuditOperationStepTypeEnum.PRECO,
        };
        delete auditOPData.oldValue;
        if (auditOperation && auditId) {
            auditsOperationService.optimizeAuditOperation(auditData.curentAuditOperation.id as number, auditOPData).subscribe({
                next: () => {
                    enqueueSnackbar(t('audits.preco.optimizationSuccess'), { variant: 'success' });
                    handleClose();
                },
                error: (err) => enqueueSnackbar(err.text, err.options),
            });
        }
    };


    return (
        <>
            <Drawer anchor="right" open onClose={handleClose}>
                <Stack overflow="hidden" height="100%">
                    <Stack direction="row" justifyContent="flex-end">
                        <IconButton onClick={handleClose}>
                            <Close />
                        </IconButton>
                    </Stack>
                    <Stack pr="10px" py='15px' flexGrow={1} display="flex" flexDirection="column" className='scrollable'
                        overflow='auto'>
                        <Stack direction="row" alignItems="center" mb="15px" spacing={1.5}>
                            <Typography fontSize={24} fontWeight="700">
                                {isOptimization ? t('audits.preco.optimizationPreco') : t('audits.preco.addContainer')}
                            </Typography>
                        </Stack>
                        <Divider variant="fullWidth" orientation="horizontal" />
                        <AuditContainer errors={errors} isPreco={true} auditOperation={auditOperation}
                            updateAuditOperation={updateAuditOperation} />
                        <Stack flexGrow={1} />
                        <Stack mt="3px" direction="row" spacing={2} justifyContent="center" mb={1}>
                            <Button variant="contained" color="primary"
                                onClick={() => isOptimization ? optimizeAuditOperation() : createAuditOperation()}
                                disabled={checkIfErrors(errors)}
                            >
                                {isOptimization ? t('global.optimize') : t('global.add')}
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </Drawer>
            <Outlet />
        </>
    );
};

export default AddPrecoContainerDrawer;
